import { Controller } from "@hotwired/stimulus"

/**
 * LinkedDatalistController. Use together with e.g. LinkedCustomSelectFieldComponent.
 *
 * This is a custom implementation of datalist-like functionality.
 * Drowpdown options are always shown, like in a select field (not filtered like in datalist).
 * Additionally, user can enter custom value, like in a datalist or text input field. Options
 * shown in dropdown can be set to differ from values shown after user has selected an option
 * (e.g. to show shorter values in field than in dropdown). Also, a second such field can be defined
 * as a linked, "b", field, which will be automatically updated when an option is selected.
 *
 * One dropdown option should be a special value, e.g. "Enter custom input". When user
 * klicks on this, the field changes to a text input field. From this, klick on "down arrow"
 * icon returns to select field.
 *
 * Expected DOM:
 * - wrapper div with attributes as shown in static values, from which this controller is called.
 * - selectOptions should be an array of json objects with the following keys:
 *       id_a, selected_label_a, selection_label_a, id_b, selected_label_b, selection_label_b.
 * - within the wrapper div, there should be the following elements:
 *    - select field. one of the select options should have the value passed as option customSelectValue
 *    - div field with class "select-overlay", which is an overlay over the select field and will hold
 *      selected_label value.
 *    - a text input field with class "custom-text-input-field" and initially hidden
 *    - a span with class "toggle-select" and initially hidden - this will be the clickable icon to return
 *        to the select field.
 * - select field and text input field must have same field name! (one will always be disabled, so only one
 *   value is passed to controller).
 * - if there is a linked field, the same elements must be present within the wrapper div for this field.
 */
export default class extends Controller {
    static values = {
        aFieldWrapperId: String,
        bFieldWrapperId: String,
        customSelectValue: String,
        selectOptions: Array
    }

    connect() {
        this.boundOnChange = this.onChange.bind(this)
        this.boundEnableSelect = this.enableSelect.bind(this)

        this.a_select_field = this.element.querySelector("select")
        if (!this.a_select_field ) { return }
        this.a_select_field.addEventListener("change", this.boundOnChange)

        this.a_toggle_select = this.element.querySelector(".toggle-select")
        this.a_toggle_select.addEventListener("click", this.boundEnableSelect)

        if (this.a_select_field.disabled) { this.enableTextInput() }
    }

    disconnect() {
        if (this.a_select_field) {
            this.a_select_field.removeEventListener("change", this.boundOnChange)
        }

        if (this.a_text_field) {
            this.a_text_field.removeEventListener("input", this.boundOnManualInput)
        }

        if (this.a_toggle_select) {
            this.a_toggle_select.removeEventListener("click", this.boundEnableSelect)
        }

        this._a_select_overlay = null;
        this._a_text_field = null;
        this._a_toggle_select = null;

        this._b_field_wrapper = null;
        this._b_select_field = null;
        this._b_select_overlay = null;
        this._b_text_field = null;
        this._b_toggle_select = null;
    }

    // look up the option's data and fill associated fields.
    onChange() {
        const value = this.a_select_field.value

        if (value === this.customSelectValueValue) {
            this.enableTextInput()
            return
        }

        const option = this._findOption(value)

        if (option) {
            this.a_select_overlay.textContent = option.selected_label_a

            if (this.b_field_wrapper) {
                this.b_select_field.value = option.id_b
                this.b_select_overlay.textContent = option.selected_label_b
                // this.b_id_field.value = option.id_b
            }
        } else {
            this.a_select_field.value = ""
            this.a_select_overlay.textContent = ""

            if (this.b_field_wrapper) {
                this.b_select_field.value = ""
                this.b_select_overlay.textContent = ""
            }
        }
    }

    enableTextInput() {
        this.a_select_field.classList.add("hide")
        this.a_select_field.disabled = true
        this.a_select_field.value = ""

        this.a_select_overlay.classList.add("hide")
        this.a_select_overlay.textContent = ""

        this.a_toggle_select.classList.remove("hide")
        this.a_text_field.classList.remove("hide")
        this.a_text_field.disabled = false
        this.a_text_field.focus()

        if (this.b_field_wrapper) {
            this.b_select_field.classList.add("hide")
            this.b_select_field.disabled = true
            this.b_select_field.value = ""

            this.b_select_overlay.classList.add("hide")
            this.b_select_overlay.textContent = ""

            this.b_text_field.classList.remove("hide")
            this.b_text_field.disabled = false

            this.b_toggle_select.classList.remove("hide")
        }
    }

    enableSelect() {
        this.a_toggle_select.classList.add("hide")
        this.a_text_field.classList.add("hide")
        this.a_text_field.disabled = true

        this.a_select_overlay.classList.remove("hide")
        this.a_select_field.classList.remove("hide")
        this.a_select_field.disabled = false

        this.a_select_field.focus()

        if (this.b_field_wrapper) {
            this.b_toggle_select.classList.add("hide")
            this.b_text_field.classList.add("hide")
            this.b_text_field.disabled = true

            this.b_select_overlay.classList.remove("hide")
            this.b_select_field.classList.remove("hide")
            this.b_select_field.disabled = false
        }
    }

    _findOption(value) {
        if (!this.hasSelectOptionsValue || !Array.isArray(this.selectOptionsValue)) return null
        return this.selectOptionsValue.find(opt => opt.id_a == value)
    }

    get a_text_field() {
        return this._a_text_field ||= this.element.querySelector("input.custom-text-input-field")
    }

    get a_select_overlay() {
        return this._a_select_overlay ||= this.element.querySelector(".select-overlay")
    }

    get b_field_wrapper() {
        if (!this.hasOwnProperty('_b_field_wrapper')) {
            this._b_field_wrapper = document.getElementById(this.bFieldWrapperIdValue) || null;
        }
        return this._b_field_wrapper;
    }

    get b_select_field() {
        const wrapper = this.b_field_wrapper;
        return wrapper ? (this._b_select_field ||= wrapper.querySelector("select")) : null;
    }

    get b_select_overlay() {
        const wrapper = this.b_field_wrapper;
        return wrapper ? (this._b_select_overlay ||= wrapper.querySelector(".select-overlay")) : null;
    }

    get b_text_field() {
        const wrapper = this.b_field_wrapper;
        return wrapper ? (this._b_text_field ||= wrapper.querySelector("input.custom-text-input-field")) : null;
    }

    get b_toggle_select() {
        const wrapper = this.b_field_wrapper;
        return wrapper ? (this._b_toggle_select = wrapper.querySelector(".toggle-select")) : null;
    }
}